import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageCircle, MoreVertical, Edit, Trash2, Save, X, Camera, Flag, Share2, Send, Check } from 'lucide-react';
import { ShareSheet } from '../ui/ShareSheet';
import { BookmarkButton } from '../BookmarkButton';
import { Avatar } from '../Profile/Avatar';
import { Username } from '../Profile/Username';
import { ConversationCommentSection } from './ConversationCommentSection';
import { formatDate } from '../../lib/utils/dateUtils';
import { supabase } from '../../lib/supabase';
import { uploadMedia } from '../../lib/storage';
import { sendMessage } from '../../lib/chat';
import { renderConversationContentWithMentions } from '../../lib/utils/mentionUtils';
import { ConversationRichContent } from './ConversationRichContent';
import { UserMentionInput } from '../ui/UserMentionInput';
import { useAdmin } from '../../lib/hooks/useAdmin';
import type { ConversationPost as ConversationPostType } from '../../types/conversations';
import { useConfirm } from '../../hooks/useConfirm';
import { toast } from 'sonner';

interface ConversationPostProps {
  post: ConversationPostType;
  onDelete?: () => void;
  expanded?: boolean;
}

export function ConversationPost({ post, onDelete, expanded = false }: ConversationPostProps) {
  const navigate = useNavigate();
  const postDetailPath = `/conversations/post/${post.slug || post.id}`;
  const [showComments, setShowComments] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(post.title);
  const [editContent, setEditContent] = useState(post.content);
  const [editImages, setEditImages] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [isOwnPost, setIsOwnPost] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [commentCount, setCommentCount] = useState(post.conversation_comments?.[0]?.count || 0);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const postRef = React.useRef<HTMLDivElement>(null);
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [showSendTo, setShowSendTo] = useState(false);
  const [chatConversations, setChatConversations] = useState<any[]>([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [sentTo, setSentTo] = useState<Set<string>>(new Set());
  const sendToRef = React.useRef<HTMLDivElement>(null);
  const [playingVideoIdx, setPlayingVideoIdx] = useState<number | null>(null);
  const { isAdmin } = useAdmin();
  const { confirm, ConfirmDialog } = useConfirm();

  React.useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setIsOwnPost(user?.id === post.user_id);
    });

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
      if (sendToRef.current && !sendToRef.current.contains(event.target as Node)) {
        setShowSendTo(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [post.user_id]);

  const fetchChatConversations = async () => {
    setLoadingChats(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: conversations, error } = await supabase
        .from('conversations')
        .select('*')
        .contains('participant_ids', [user.id])
        .order('last_message_at', { ascending: false })
        .limit(15);

      if (error) throw error;
      if (!conversations) return;

      const enriched = await Promise.all(
        conversations.map(async (conv: any) => {
          if (conv.type === 'direct') {
            const otherId = conv.participant_ids.find((id: string) => id !== user.id);
            if (otherId) {
              const { data: otherUser } = await supabase
                .from('users')
                .select('id, username, avatar_url')
                .eq('id', otherId)
                .single();
              return { ...conv, otherUser };
            }
          }
          return conv;
        })
      );

      setChatConversations(enriched);
    } catch (error) {
      console.error('Error fetching chat conversations:', error);
    } finally {
      setLoadingChats(false);
    }
  };

  const handleSendToChat = async (conversationId: string) => {
    try {
      const postSlug = post.slug || post.id;
      const postUrl = `${window.location.origin}/conversations/post/${postSlug}`;
      const messageContent = `📝 ${post.title}\n${postUrl}`;

      await sendMessage(conversationId, messageContent);
      setSentTo(prev => new Set(prev).add(conversationId));
      toast.success('Post sent!');
    } catch (error) {
      console.error('Error sending post to chat:', error);
      toast.error('Failed to send post');
    }
  };

  const handleToggleSendTo = () => {
    if (!showSendTo) fetchChatConversations();
    setShowSendTo(!showSendTo);
  };

  const handleSubmitReport = async () => {
    if (!reportReason) {
      toast.error('Please select a reason for reporting');
      return;
    }
    try {
      setSubmittingReport(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to report');
        return;
      }
      const { error } = await supabase.from('reports').insert([{
        reporter_id: user.id,
        content_type: 'conversation_post',
        content_id: post.id,
        reason: reportReason,
        description: reportDescription || null
      }]);
      if (error) throw error;
      toast.success('Report submitted. Thank you for helping keep our community safe.');
      setShowReportModal(false);
      setReportReason('');
      setReportDescription('');
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report');
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditTitle(post.title);
    setEditContent(post.content);
    setEditImages([]);
    setShowMenu(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditTitle(post.title);
    setEditContent(post.content);
    setEditImages([]);
    setMentionedUsers([]);
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim() || !editContent.trim()) return;

    setSaving(true);
    try {
      const newImageUrls = await Promise.all(
        editImages.map(async (file) => {
          const isVideo = file.type.startsWith('video/');
          const bucket = isVideo ? 'post-videos' : 'post-images';
          try {
            return await uploadMedia(file, bucket);
          } catch (error) {
            console.error('Error uploading file:', error);
            throw error;
          }
        })
      );

      const allImages = [...(post.images || []), ...newImageUrls.filter(Boolean)];

      const { error } = await supabase
        .from('conversation_posts')
        .update({
          title: editTitle,
          content: editContent,
          images: allImages,
          updated_at: new Date().toISOString()
        })
        .eq('id', post.id);

      if (error) throw error;

      post.title = editTitle;
      post.content = editContent;
      post.images = allImages;
      setIsEditing(false);
      setEditImages([]);
      setMentionedUsers([]);
      toast.success('Post updated successfully');
    } catch (error) {
      console.error('Error updating post:', error);
      toast.error('Failed to update post');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async () => {
    const confirmed = await confirm({
      title: "Delete Post",
      description: "Are you sure you want to delete this post? This action cannot be undone.",
      confirmText: "Delete",
      variant: "destructive"
    });

    if (!confirmed) return;

    try {
      setIsDeleting(true);

      if (postRef.current) {
        postRef.current.style.transition = 'all 0.3s ease-out';
        postRef.current.style.opacity = '0';
        postRef.current.style.transform = 'translateY(-20px)';
      }

      await new Promise(resolve => setTimeout(resolve, 300));

      const { error } = await supabase
        .from('conversation_posts')
        .delete()
        .eq('id', post.id);

      if (error) throw error;

      toast.success('Post deleted successfully');
      onDelete?.();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
      if (postRef.current) {
        postRef.current.style.opacity = '1';
        postRef.current.style.transform = 'translateY(0)';
      }
      setIsDeleting(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(
      (f) => f.type.startsWith('image/') || f.type.startsWith('video/')
    );
    if (files.length + editImages.length > 4) {
      toast.error('Maximum 4 media files allowed');
      return;
    }
    setEditImages(prev => [...prev, ...files]);
  };

  const removeEditImage = (index: number) => {
    setEditImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index: number) => {
    if (post.images) {
      post.images = post.images.filter((_, i) => i !== index);
    }
  };

  /** Feed preview: square (1:1) tiles, clean frame */
  const renderCompactMediaPreview = () => {
    if (!post.images || post.images.length === 0) return null;

    const total = post.images.length;
    const slice = post.images.slice(0, 4);
    const frame =
      'w-full max-w-[min(100%,280px)] rounded-xl overflow-hidden ring-1 ring-black/[0.06] shadow-sm';

    const isVideoUrl = (url: string) =>
      url.includes('.mp4') ||
      url.includes('.mov') ||
      url.includes('.webm') ||
      url.includes('.avi') ||
      url.includes('/post-videos/') ||
      url.includes('video%2F');

    const renderCell = (media: string, idx: number) => {
      const isVideo = isVideoUrl(media);
      const showMoreOverlay = idx === 3 && total > 4;
      const isPlaying = playingVideoIdx === idx;

      const handleVideoClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (isVideo) {
          setPlayingVideoIdx(isPlaying ? null : idx);
        }
      };

      return (
        <div
          key={idx}
          onClick={handleVideoClick}
          className="relative aspect-square bg-gray-100 overflow-hidden cursor-pointer"
        >
          {isVideo ? (
            <video
              src={media}
              className="absolute inset-0 h-full w-full object-cover"
              muted={!isPlaying}
              loop
              playsInline
              preload={isPlaying ? 'auto' : 'metadata'}
              autoPlay={isPlaying}
            />
          ) : (
            <img src={media} alt="" className="absolute inset-0 h-full w-full object-cover" />
          )}
          {isVideo && !isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
              <div className="w-12 h-12 bg-black/70 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          )}
          {isVideo && isPlaying && (
            <div className="absolute top-2 right-2">
              <div className="w-7 h-7 bg-black/60 rounded-full flex items-center justify-center">
                <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
              </div>
            </div>
          )}
          {showMoreOverlay && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span className="text-white text-lg font-semibold">+{total - 4}</span>
            </div>
          )}
        </div>
      );
    };

    if (total === 1) {
      return <div className={`mt-3 ${frame}`}>{renderCell(slice[0], 0)}</div>;
    }

    if (total === 2) {
      return (
        <div className={`mt-3 ${frame}`}>
          <div className="grid grid-cols-2 gap-0.5">{slice.map((m, i) => renderCell(m, i))}</div>
        </div>
      );
    }

    if (total === 3) {
      return (
        <div className={`mt-3 flex flex-col gap-0.5 ${frame}`}>
          {renderCell(slice[0], 0)}
          <div className="grid grid-cols-2 gap-0.5">
            {renderCell(slice[1], 1)}
            {renderCell(slice[2], 2)}
          </div>
        </div>
      );
    }

    return (
      <div className={`mt-3 ${frame}`}>
        <div className="grid grid-cols-2 gap-0.5">
          {slice.map((m, i) => renderCell(m, i))}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={postRef}
      className="border-b border-gray-200 py-4"
      style={{ opacity: 1, transform: 'translateY(0)' }}
    >
      {/* ── Row: avatar + all content ── */}
      <div className="flex items-start gap-3">

        {/* Left col: avatar */}
        <Link to={`/profile/${post.user?.username}/listings`} className="flex-shrink-0 mt-0.5">
          <Avatar
            url={post.user?.avatar_url}
            size="md"
            username={post.user?.username}
            editable={false}
          />
        </Link>

        {/* Right col: everything */}
        <div className="flex-1 min-w-0">

          {/* Header: name · date + menu */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 min-w-0">
              <Link
                to={`/profile/${post.user?.username}/listings`}
                className="font-semibold text-content hover:underline truncate"
              >
                <Username
                  username={post.user?.username || 'Anonymous'}
                  userId={post.user_id}
                  verified={post.user?.verified}
                />
              </Link>
              <span className="text-gray-400">·</span>
              <span className="text-gray-500 text-sm whitespace-nowrap">
                {formatDate(new Date(post.created_at))}
              </span>
            </div>

            {/* Three-dot menu — visible to all users */}
            <div className="relative flex-shrink-0 ml-2" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                disabled={isDeleting}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {showMenu && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg py-1 z-10 border border-gray-200">
                  {isOwnPost && (
                    <button
                      onClick={handleEdit}
                      className="w-full text-left px-4 py-2 text-sm text-content hover:bg-gray-50 transition-colors flex items-center"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </button>
                  )}
                  {(isOwnPost || isAdmin) && (
                    <button
                      onClick={handleDeletePost}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center"
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                  )}
                  {!isOwnPost && (
                    <button
                      onClick={() => { setShowReportModal(true); setShowMenu(false); }}
                      className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors flex items-center"
                    >
                      <Flag className="h-4 w-4 mr-2" />
                      Report
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Body ── */}
          {isEditing ? (
            <div className="mt-3 space-y-3">
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-lg focus:border-accent-text focus:ring-1 focus:ring-accent-text/20 font-semibold"
                placeholder="Post title"
              />
              <UserMentionInput
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onMentionedUsersChange={setMentionedUsers}
                className="w-full p-3 border border-gray-200 rounded-lg focus:border-accent-text focus:ring-1 focus:ring-accent-text/20 resize-none"
                rows={4}
                placeholder="What's on your mind?"
              />

              {post.images && post.images.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-content">Current Media:</h4>
                  <div className="flex flex-wrap gap-2">
                    {post.images.map((image, index) => {
                      const isVideo = image.includes('.mp4') || image.includes('.mov') || image.includes('.webm') || image.includes('.avi') || image.includes('/post-videos/') || image.includes('video%2F');
                      return (
                      <div key={index} className="relative">
                        {isVideo ? (
                          <video src={image} className="h-16 w-16 object-cover rounded-lg" muted />
                        ) : (
                          <img src={image} alt={`Current ${index + 1}`} className="h-16 w-16 object-cover rounded-lg" />
                        )}
                        <button
                          type="button"
                          onClick={() => removeExistingImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                        >×</button>
                      </div>
                    )})}
                  </div>
                </div>
              )}

              {editImages.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-content">New Media:</h4>
                  <div className="flex flex-wrap gap-2">
                    {editImages.map((file, index) => {
                      const isVideo = file.type.startsWith('video/');
                      return (
                      <div key={index} className="relative">
                        {isVideo ? (
                          <video src={URL.createObjectURL(file)} className="h-16 w-16 object-cover rounded-lg" muted />
                        ) : (
                          <img src={URL.createObjectURL(file)} alt={`New ${index + 1}`} className="h-16 w-16 object-cover rounded-lg" />
                        )}
                        <button
                          type="button"
                          onClick={() => removeEditImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                        >×</button>
                      </div>
                    )})}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" onChange={handleImageUpload} className="hidden" />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center text-sm text-accent-text hover:text-accent-text/80"
                >
                  <Camera className="h-4 w-4 mr-1" />
                  Add Media
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1.5 text-sm text-gray-500 hover:text-content rounded-md flex items-center"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={saving || !editTitle.trim() || !editContent.trim()}
                    className="px-3 py-1.5 text-sm bg-accent-text text-white rounded-md hover:bg-accent-text/90 disabled:opacity-50 flex items-center"
                  >
                    {saving ? (
                      <><div className="animate-spin rounded-full h-3 w-3 border-b border-white mr-1" />Saving...</>
                    ) : (
                      <><Save className="h-4 w-4 mr-1" />Save</>
                    )}
                  </button>
                </div>
              </div>
            </div>

          ) : expanded ? (
            /* Expanded view */
            <>
              <h3 className="text-lg font-bold text-content mt-0.5 mb-1">
                {post.title}
              </h3>
              <ConversationRichContent
                className="text-content/90 whitespace-pre-line leading-relaxed"
                html={renderConversationContentWithMentions(post.content)}
              />
            </>

          ) : (
            /* Compact / feed view — avoid <a> inside <Link> (mentions + external URLs) */
            <div
              className="group/title block mt-0.5 cursor-pointer rounded-lg -m-1 p-1"
              role="link"
              tabIndex={0}
              aria-label={post.title}
              onClick={(e) => {
                const el = e.target as HTMLElement;
                if (el.closest('a')) return;
                navigate(postDetailPath);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                if ((e.target as HTMLElement).closest('a')) return;
                e.preventDefault();
                navigate(postDetailPath);
              }}
            >
              <h3 className="font-bold text-content group-hover/title:text-accent-text transition-colors">
                {post.title}
              </h3>
              {post.content && (
                <ConversationRichContent
                  className="text-content/70 text-sm mt-1 line-clamp-3 whitespace-pre-line"
                  html={renderConversationContentWithMentions(post.content)}
                />
              )}
              {renderCompactMediaPreview()}
            </div>
          )}

          {/* Images grid — expanded only */}
          {expanded && post.images && post.images.length > 0 && !isEditing && (
            <div className={`mt-3 grid gap-2 rounded-xl overflow-hidden ${
              post.images.length === 1 ? 'grid-cols-1' :
              post.images.length === 2 ? 'grid-cols-2' :
              post.images.length === 3 ? 'grid-cols-2 grid-rows-2' :
              'grid-cols-2'
            }`}>
              {post.images.map((image, index) => {
                const isVideo = image.includes('.mp4') || image.includes('.mov') || image.includes('.webm') || image.includes('.avi') || image.includes('/post-videos/') || image.includes('video%2F');
                return (
                <div
                  key={index}
                  className={`relative overflow-hidden rounded-lg ${
                    post.images!.length === 3 && index === 0 ? 'row-span-2' : ''
                  }`}
                >
                  {isVideo ? (
                    <video
                      src={image}
                      className="w-full aspect-square object-cover"
                      controls
                      muted
                      loop
                      preload="metadata"
                      playsInline
                    />
                  ) : (
                    <img
                      src={image}
                      alt={`Post image ${index + 1}`}
                      className="w-full aspect-square object-cover"
                    />
                  )}
                </div>
              )})}
            </div>
          )}

          {/* ── Action bar ── */}
          <div className="flex items-center gap-4 mt-3">
            {/* Comments */}
            {expanded ? (
              <button
                onClick={() => setShowComments(!showComments)}
                className="flex items-center gap-1.5 text-sm text-content/50 hover:text-accent-text transition-colors group"
              >
                <MessageCircle className="h-4 w-4 group-hover:scale-110 transition-transform" />
                <span>{commentCount}</span>
              </button>
            ) : (
              <Link
                to={`/conversations/post/${post.slug || post.id}`}
                className="flex items-center gap-1.5 text-sm text-content/50 hover:text-accent-text transition-colors group"
              >
                <MessageCircle className="h-4 w-4 group-hover:scale-110 transition-transform" />
                <span>{commentCount}</span>
              </Link>
            )}

            {/* Bookmark — shown on both views */}
            <BookmarkButton targetId={post.id} targetType="conversation_posts" />

            {/* Send To — shown on both views */}
            <div className="relative" ref={sendToRef}>
              <button
                onClick={handleToggleSendTo}
                className="flex items-center gap-1.5 text-sm text-content/50 hover:text-accent-text transition-colors group"
              >
                <Send className="h-4 w-4 group-hover:scale-110 transition-transform" />
                <span>Send to</span>
              </button>
              {showSendTo && (
                <div className="absolute left-0 bottom-full mb-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 z-20 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-content">Send to chat</p>
                    <p className="text-xs text-gray-500">Select a conversation</p>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {loadingChats ? (
                      <div className="flex justify-center p-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-accent-text" />
                      </div>
                    ) : chatConversations.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">No conversations yet</div>
                    ) : (
                      chatConversations.map((conv: any) => {
                        const isSent = sentTo.has(conv.id);
                        const displayName = conv.type === 'group' ? conv.name : conv.otherUser?.username;
                        const avatarUrl = conv.type === 'group' ? conv.image_url : conv.otherUser?.avatar_url;
                        return (
                          <button
                            key={conv.id}
                            onClick={() => !isSent && handleSendToChat(conv.id)}
                            disabled={isSent}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${isSent ? 'bg-accent-text/5' : 'hover:bg-accent-text/5'}`}
                          >
                            <Avatar url={avatarUrl} size="sm" username={displayName} editable={false} />
                            <span className="flex-1 text-sm font-medium text-content truncate">{displayName || 'Unknown'}</span>
                            {isSent
                              ? <Check className="h-4 w-4 text-accent-text flex-shrink-0" />
                              : <Send className="h-3.5 w-3.5 text-content/40 flex-shrink-0" />
                            }
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Share button — bottom sheet on mobile, copy link on PC */}
            <button
              onClick={() => {
                const isMobile = window.innerWidth < 768;
                const postUrl = `${window.location.origin}/conversations/post/${post.slug || post.id}`;
                if (isMobile) {
                  setShowShareSheet(true);
                } else {
                  navigator.clipboard.writeText(postUrl);
                  toast.success('Link copied to clipboard');
                }
              }}
              className="flex items-center gap-1.5 text-sm text-content/50 hover:text-accent-text transition-colors group"
            >
              <Share2 className="h-4 w-4 group-hover:scale-110 transition-transform" />
              <span>Share</span>
            </button>
          </div>

          {/* Share Sheet */}
          <ShareSheet
            isOpen={showShareSheet}
            onClose={() => setShowShareSheet(false)}
            title={post.title}
            text={post.content?.slice(0, 100) || post.title}
            url={`${window.location.origin}/conversations/post/${post.slug || post.id}`}
          />

          {/* Comments section — expanded only */}
          {expanded && showComments && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <ConversationCommentSection
                postId={post.id}
                onCommentAdded={() => setCommentCount(prev => prev + 1)}
                onCommentDeleted={() => setCommentCount(prev => Math.max(0, prev - 1))}
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog />

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowReportModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6 z-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-content">Report Post</h3>
              <button
                onClick={() => setShowReportModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Why are you reporting this post?</p>
            <div className="space-y-2 mb-4">
              {[
                { value: 'spam', label: 'Spam' },
                { value: 'inappropriate', label: 'Inappropriate content' },
                { value: 'harassment', label: 'Harassment or bullying' },
                { value: 'misinformation', label: 'Misinformation' },
                { value: 'other', label: 'Other' }
              ].map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    reportReason === reason.value
                      ? 'border-accent-text bg-accent-text/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="reportReason"
                    value={reason.value}
                    checked={reportReason === reason.value}
                    onChange={(e) => setReportReason(e.target.value)}
                    className="sr-only"
                  />
                  <span className="text-sm text-content">{reason.label}</span>
                </label>
              ))}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional details (optional)
              </label>
              <textarea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Provide more context..."
                className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowReportModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReport}
                disabled={!reportReason || submittingReport}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingReport ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}